<script setup>
import DateFormattedTextFieldFK from '#common/components/DateFormattedTextFieldFK.vue'
import ErrorList from '#common/components/ErrorList.vue'

const { $store, $auth } = useNuxtApp()
const $route = useRoute()
const values = ref({
  email: '',
  default_zipcode: '',
})
const theme = ref(
  Object.assign(
    {
      register: {
        submitButton: {
          classes: '',
          label: 'Register',
        },
      },
    },
    $auth.authModalTheme,
  ),
)
const firstNameInput = ref(null)
const registrationErrors = ref({})
const allErrors = computed(() => {
  const regErrors = registrationErrors.value
    ? Object.values(registrationErrors.value).flat()
    : []
  const checkUserErrors = $auth.checkUserExistsError?.value
    ? Object.values($auth.checkUserExistsError.value).flat()
    : []

  return [...regErrors, ...checkUserErrors]
})
const loading = computed(() => $store.getters.loading)
const recaptchaComponent = ref(null)

watch(
  () => $auth.authZipcode,
  (newValue) => {
    values.value.default_zipcode ||= newValue
  },
)
onMounted(() => {
  firstNameInput.value.$el.querySelector('input').focus()
  values.value.email = $auth.authEmail
  values.value.default_zipcode = $auth.authZipcode
})
async function register() {
  registrationErrors.value = {}
  const recaptchaResponse = recaptchaComponent.value?.getRecaptchaResponse()
  if (recaptchaResponse) {
    values.value.recaptcha = recaptchaResponse
  }
  values.value.referrer = $route.fullPath
  try {
    await $auth.registerUser(values.value)
    if (recaptchaResponse) {
      recaptchaComponent.value.resetRecaptcha()
    }
    $auth.setAuthStep('email-verify')
  } catch (e) {
    console.log('register.error', e)
    if (e.errors) {
      registrationErrors.value = e.errors
    }
    recaptchaComponent.value?.resetRecaptcha()
  }
}
function checkEmail() {
  values.value.email = values.value.email.trim()
  $auth.checkUserExists(values.value.email)
}
</script>

<template>
  <FormKit v-model="values" type="form" :actions="false" @submit="register">
    <slot name="title">
      <h2>Create Your Account</h2>
    </slot>
    <FormKit
      ref="emailInput"
      type="text"
      name="email"
      label="Email"
      validation="bail|required:trim|email"
      @blur="checkEmail"
    />
    <FormKit
      ref="firstNameInput"
      type="text"
      name="first_name"
      label="First Name"
      validation="required"
    />
    <FormKit
      type="text"
      name="last_name"
      label="Last Name"
      validation="required"
    />
    <FormKit
      type="text"
      name="default_zipcode"
      label="Zip Code"
      validation="required|matches:/^\d{5}(?:-\d{4})?$/"
      :validation-messages="{
        matches: 'Error: You must input a valid US Zip Code',
      }"
    />
    <DateFormattedTextFieldFK
      name="date_of_birth"
      label="Birthday"
      class="formulate-input-label"
      validation="bail|required|date:YYYY-MM-DD|livingBirthday|legalBirthday"
      :max="moment().format('YYYY-MM-DD')"
    />
    <FormKit
      type="password"
      name="password"
      label="Password"
      validation="^required|length:12|matches:/[0-9]/|contains_alpha"
      validation-visibility="dirty"
      :validation-messages="{
        matches: 'Error: Passwords require at least one number.',
      }"
    />
    <ErrorList :errors="allErrors" />
    <Recaptcha ref="recaptchaComponent" />
    <FormKit
      :disabled="loading"
      type="submit"
      :input-class="theme.register.submitButton.classes"
      :label="theme.register.submitButton.label"
    />
    <div
      class="button button-white hidden-for-medium-up"
      style="margin-top: -10px"
      @click="$auth.closeAuthModal()"
    >
      Cancel
    </div>
    <p>
      Not You?
      <a @click="$auth.setAuthStep('email-check')">Try Another Email</a>
    </p>
  </FormKit>
</template>
